import Vue from 'vue';
import pick from 'lodash/pick';
import mergeWith from 'lodash/mergeWith';
import {
	ADD_EVENT_TAG,
	DELETE_EVENT_TAG,
	PATCH_EVENT,
	SET_ACTUAL_STREAM,
	SET_EVENT,
	SET_EVENT_ADS,
	SET_EVENT_BANNER_IMAGE_URL,
	SET_EVENT_LINEUP,
	SET_EVENT_PERMISSIONS,
	SET_EVENT_STATS,
	SET_EVENT_TAGS,
	SET_GEOBLOCKED,
	SET_HIGHLIGHT_RULES,
	SET_LEAGUE_TABLE,
	SET_LOADED_STATUS,
	SET_MATCHDAY_EVENT_CHANGE,
	SET_MATCHDAY_EVENTS,
	SET_MATCHDAY_REFERENCE_EVENT,
	SET_PASSWORD,
	SET_PERIODS,
	SET_PLAYER_CUSTOM_TEXT,
	SET_PLAYER_CUSTOM_TEXT_ENABLED,
	SET_PREVIEW_LIMIT_REACHED,
	SET_SCOREBOARD_SETTINGS,
	SET_SORT_KEY,
	SET_STATE,
	SET_STREAM,
	SET_TAG_TEMPLATES,
	UPDATE_EVENT_SCORE,
	UPDATE_EVENT_TAG,
} from '../../constants/mutations';
import { CHILD_MATCH_LEAGUE } from '../../constants/categories';
import api, { EVENT_RELATIONS } from '../../api';
import { bus, EVENT, playerBus } from '@videotools/js/events';
import Event from '../../api/entities/Event';
import EventTag from '../../api/entities/EventTag';
import { GAME_SPORT, SCOREBOARD_SUPPORTED_PARENT_CATEGORIES } from '@shared/constants/game';
import { EVENT_VISIBILITY } from '../../constants/event';
import { getStoredTagGroups } from '@videotools/js/store/modules/tag-groups';
import { PLAYOUT_API_ERRORS, TAG_BEFORE_CREATE, TAG_CREATE } from '@videotools/js/events/enum';
import { ERROR } from '@videotools/js/logic/error-screen/ErrorScreen';
import { heartbeat } from '../../events/modules/heartbeat';
import { gameSecondsToGameTime, getGameTime, getPeriods, PERIOD_TYPE } from '@videotools/js/logic/game';
import { AdType } from '@shared/enums/AdType';
import { APP_TYPE } from '@videotools/js/constants/app';

const getInitialState = () => ({
	ads:                    [],
	event:                  null,
	tags:                   [],
	stream:                 null,
	lineup:                 [],
	eventStats:             null,
	eventPermissions:       [],
	leagueTable:            null,
	matchdayEvents:         [],
	matchdayReferenceEvent: null,
	playerLineupStats:      null,
	videos:                 [],
	videosSortKey:          'recent',
	loadedStatus:           {},
	password:               null,
	unixTime:               null,
	unixTimeTicker:         -1,
	highlightRules:         null,
	matchdayEventChanged:   false,
	eventBannerImageUrl:    null,
	isGeoblocked:           false,
	previewLimitReached:    false,
	tagTemplates:           [],
	adBanner:               null,
});

const hasTag = (tags, id) => tags.some((tag) => tag.id === id);

const patchEvent = (prev, next) => {
	return mergeWith({ ...prev }, { ...next }, (left, right) => {
		if ([left, right].some((member) => !Array.isArray(member))) {
			if (typeof right !== 'undefined') return right;
			if (typeof right !== 'undefined') return left;
			return undefined;
		}
		if (right.every((item) => !item.id)) return right;
		const items = [];
		const map = new Map();
		for (const item of [...left, ...right]) {
			if (!item.id) {
				items.push(item);
				continue;
			}
			if (map.has(item.id)) {
				map.set(item.id, patchEvent(map.get(item.id), item));
			} else {
				map.set(item.id, item);
			}
		}
		items.push(...map.values());
		return items;
	});
};

export default {
	namespaced: true,
	state:      getInitialState,
	mutations:  {
		[SET_STATE]: (state, value) => {
			for (const [k, v] of Object.entries(value)) {
				state[k] = v;
			}
		},
		[SET_EVENT]: (state, value) => {
			state.event = value;

			const vodAvailable = state.event?.streams?.[0]?.vodStatus === 'available';

			// start relative time ticker for live events
			// This ensures isLive getter updates reactively when event ends
			clearInterval(state.unixTimeTicker);
			state.unixTime = null;

			if (!state.event.vodOnly && !vodAvailable) {
				state.unixTime = Math.floor(Date.now() / 1000);
				state.unixTimeTicker = setInterval(() => {
					state.unixTime = Math.floor(Date.now() / 1000);
				}, 1000);
			}
		},
		[PATCH_EVENT]: (state, value) => {
			const eventUpdates = { ...value };
			// The event URL belongs to the viewer's Arena domain, while broadcasts can be generated under another host.
			delete eventUpdates.url;
			state.event = new Event(patchEvent(state.event, eventUpdates));
		},
		[SET_EVENT_TAGS]: (state, tags) => {
			state.tags = tags;
		},
		[ADD_EVENT_TAG](state, tag) {
			const existingTag = hasTag(state.tags, tag.id);
			if (!existingTag) state.tags.push(tag);
		},
		[UPDATE_EVENT_TAG]: (state, value) => {
			const tagIndex = state.tags.findIndex((tag) => tag.id === value.id);

			if (tagIndex === -1) {
				console.log('Unable to find event tag to update.');
			} else {
				const tag = state.tags[tagIndex];
				state.tags.splice(tagIndex, 1, Object.assign(tag, value));
			}
		},
		[DELETE_EVENT_TAG]: (state, value) => {
			state.tags = state.tags.filter((tag) => tag.id !== value.id);
			bus.publish(EVENT.TAG_DELETE, value);
		},
		[SET_STREAM](state, { index, value }) {
			Vue.set(state.event.streams, index, value);
		},
		[SET_ACTUAL_STREAM](state, value) {
			state.stream = value;
		},
		[SET_EVENT_LINEUP](state, eventLineup) {
			state.lineup = eventLineup;
		},
		[SET_EVENT_PERMISSIONS](state, value) {
			state.eventPermissions = value;
		},
		[SET_HIGHLIGHT_RULES](state, value) {
			state.highlightRules = value;
		},
		[SET_EVENT_STATS](state, stats) {
			state.eventStats = stats;
		},
		[SET_LEAGUE_TABLE](state, leagueTable) {
			state.leagueTable = leagueTable;
		},
		[SET_LOADED_STATUS](state, { name, status }) {
			Vue.set(state.loadedStatus, name, status);
		},
		[SET_MATCHDAY_EVENTS](state, events) {
			state.matchdayEvents = events;
		},
		[UPDATE_EVENT_SCORE](state, { event, score, scores, finalScore }) {
			const index = state.matchdayEvents.findIndex((e) => e.id === event.id);
			if (index >= 0) {
				const eventToBeUpdated = state.matchdayEvents[index];
				if (finalScore) Vue.set(eventToBeUpdated, 'finalScore', finalScore);
				if (scores) Vue.set(eventToBeUpdated, 'scores', scores);
				if (score) Vue.set(eventToBeUpdated, 'score', score);
				Vue.set(state.matchdayEvents, index, eventToBeUpdated);
			}

			if (state.event?.id === event.id) {
				if (finalScore) Vue.set(state.event, 'finalScore', finalScore);
				if (scores) Vue.set(state.event, 'scores', scores);
				if (score) Vue.set(state.event, 'score', score);
			}
		},
		[SET_SORT_KEY](state, sortKey) {
			state.videosSortKey = sortKey;
		},
		[SET_EVENT_ADS](state, value) {
			state.ads = value || [];
		},
		[SET_SCOREBOARD_SETTINGS]: (state, value) => {
			Vue.set(state.event, 'scoreboardSettings', value);
		},
		[SET_PERIODS]: (state, value) => {
			Vue.set(state.event, 'periods', value);
		},
		[SET_MATCHDAY_REFERENCE_EVENT]: (state, value) => {
			state.matchdayReferenceEvent = value;
		},
		[SET_PASSWORD]: (state, value) => {
			state.password = value;
		},
		[SET_PLAYER_CUSTOM_TEXT]: (state, value) => {
			Vue.set(state.event, 'playerCustomText', value);
		},
		[SET_PLAYER_CUSTOM_TEXT_ENABLED]: (state, value) => {
			Vue.set(state.event, 'playerCustomTextEnabled', value);
		},
		[SET_MATCHDAY_EVENT_CHANGE]: (state, value) => {
			state.matchdayEventChanged = value;
		},
		[SET_EVENT_BANNER_IMAGE_URL]: (state, value) => {
			state.eventBannerImageUrl = value;
		},
		[SET_GEOBLOCKED]: (state) => {
			state.isGeoblocked = true;
		},
		[SET_PREVIEW_LIMIT_REACHED]: (state) => {
			state.previewLimitReached = true;
		},
		[SET_TAG_TEMPLATES]: (state, value) => {
			value.map(template => {
				template.items.map(item => {
					item.isOpen = false;
					return item;
				});
				return template;
			});
			state.tagTemplates = value;
		},
	},
	actions: {
		setState:  ({ commit }, value) => commit(SET_STATE, value),
		reset:     ({ commit }) => commit(SET_STATE, getInitialState()),
		loadEvent: async({ commit, dispatch, rootGetters }, { id }) => {
			const isTaggingTool = rootGetters['app/appType'] === APP_TYPE.TAGGING_TOOL;

			if (!Number.isFinite(Number.parseInt(id))) {
				throw new TypeError(`Cannot load event with invalid id: ${id}`);
			}

			const eventData = await api.getEvent(id, [
				EVENT_RELATIONS.SPORT,
				EVENT_RELATIONS.SPORT_CATEGORY_PARENTS,
				EVENT_RELATIONS.IMAGE,
				EVENT_RELATIONS.STREAMS,
				EVENT_RELATIONS.PARTICIPANTS,
				EVENT_RELATIONS.PARTICIPANTS_PARTICITABLE,
				EVENT_RELATIONS.PARTICIPANT_CONTEXT,
				EVENT_RELATIONS.TEAM_HOME,
				EVENT_RELATIONS.TEAM_GUEST,
				EVENT_RELATIONS.PERIODS,
				EVENT_RELATIONS.VENUE,
				EVENT_RELATIONS.LOCATION,
				EVENT_RELATIONS.CATEGORY_PARENT,
				EVENT_RELATIONS.CATEGORY_CHILD,
				EVENT_RELATIONS.LEAGUE,
				EVENT_RELATIONS.SCORES,
				isTaggingTool ? EVENT_RELATIONS.LABELS : '', // load labels in tagging tool
			].join(','));

			const data = new Event(eventData);

			// Set event permissions once
			if (eventData.can) {
				const eventPermissions = Object.keys(eventData.can).map(key => ({ key, value: eventData.can[key] }));
				commit(SET_EVENT_PERMISSIONS, eventPermissions);
			}

			await dispatch('handleEventUpdate', { data });
			await dispatch('loadEventStreamPlayout', id);

			if (!isTaggingTool) {
				await dispatch('loadEventAds', id);
			}
		},
		setEvent: async({ commit, dispatch }, event) => {
			commit(SET_EVENT, event);
			await dispatch('handleEventUpdate', { data: event });
			await Promise.all([
				await dispatch('loadEventAds', event.id),
				await dispatch('loadEventStreamPlayout', event.id),
			]);

			dispatch('loadEventBannerImage');
		},
		loadEventExtra: async({ state, dispatch, rootGetters }, { id, cached = false }) => {
			if (!Number.isFinite(Number.parseInt(id))) {
				throw new TypeError(`Cannot load event with invalid id: ${id}`);
			}

			try {
				const isPreviewMode = rootGetters['app/isPreviewMode'];
				if (!isPreviewMode) {
					dispatch('loadEventTags', { id, cached });
				}
			} finally {
				await dispatch('handleEventUpdate', { data: state.event });
			}
		},
		loadEventTags: async({ commit, getters, rootGetters }, { id, cached = false }) => {
			const selectedGroup = rootGetters['tagGroups/selectedTagGroup'];
			const showTags = rootGetters['features/showTags'];
			let selectedGroupId = selectedGroup?.id;
			if (!selectedGroupId) {
				const storedTgs = getStoredTagGroups();
				selectedGroupId = storedTgs[getters.sportId];
			}
			const tags = await api.getEventTags(
				{
					eventId: id,
					...(selectedGroupId ? { groupId: selectedGroupId } : {}),
					cached,
				},
				['tag']
			);
			commit(
				SET_EVENT_TAGS,
				tags.map((tag) => new EventTag(tag, { showTags }))
			);
		},
		changeMatchday: async({ dispatch, commit }, eventId) => {
			// Used to determine whetehr or not to play the ad again
			commit(SET_MATCHDAY_EVENT_CHANGE, true);

			await dispatch('loadEvent', { id: eventId });
			await dispatch('loadEventTags', { id: eventId, cached: true });

			const lineup = await api.getEventLineup(eventId);
			commit(SET_EVENT_LINEUP, lineup);

			// EventStats (player stats) should be updated as well,
			// but not fetching it to keep load low
			// getEventStats api.event.stats
		},
		loadEventStreamPlayout: async({ commit, dispatch, getters }, eventId) => {
			try {
				const eventStream = await api.getEventStream(eventId, getters.password);
				if (!eventStream.hlsUrl) {
					throw new Error('No stream found');
				}
				commit(SET_ACTUAL_STREAM, eventStream);
				dispatch('error/setPlayoutError', null, { root: true });
				dispatch('error/setParallelView', null, { root: true });
				await dispatch('setStreamLoaded', true);

				if (eventStream.isPreview && eventStream.previewDuration) {
					dispatch('app/setPreviewMode', eventStream.previewDuration, { root: true });
					heartbeat.setPayload({
						preview: true,
					}, true);
				}
			} catch (error) {
				switch (error?.data?.reason) {
				case PLAYOUT_API_ERRORS.PARALLEL_STREAM:
					dispatch('error/setPlayoutError', ERROR.PARALLEL_STREAM_DETECTED, { root: true });
					dispatch('error/setParallelView', error.data, { root: true });
					break;
				case PLAYOUT_API_ERRORS.PAYMENT_REQUIRED:
					dispatch('error/setPlayoutError', ERROR.EVENT_PAYMENT_REQUIRED, { root: true });
					break;
				case PLAYOUT_API_ERRORS.EVENT_GEOBLOCKED:
					dispatch('error/setPlayoutError', ERROR.EVENT_GEOBLOCKED, { root: true });
					dispatch('event/setGeoblocked', null, { root: true });

					// Non-payment protected events do not trigger a paywall, so a separate geoblocking modal is shown
					// Includes some delay because the eventBus is a separate vue app that needs to initialised
					setTimeout(function() {
						if (window.eventBus && !getters.isPaymentProtected) {
							window.eventBus.$emit('modal:GeoblockingModal');
						}
					}, 1000);

					break;
				case PLAYOUT_API_ERRORS.PREVIEW_LIMIT:
					if (getters.isLoginProtected && !getters.isPaymentProtected) {
						dispatch('error/setPlayoutError', ERROR.EVENT_AUTH_REQUIRED, { root: true });
						if (window.eventBus) {
							window.eventBus.$emit('modal:LoginWallModal');
						}
					} else {
						dispatch('error/setPlayoutError', ERROR.EVENT_PAYMENT_REQUIRED, { root: true });
						if (window.eventBus) {
							window.eventBus.$emit('modal:PaywallModal');
						}
					}
					break;
				case PLAYOUT_API_ERRORS.AUTH:
					dispatch('error/setPlayoutError', ERROR.EVENT_AUTH_REQUIRED, { root: true });
					window.eventBus.$emit('modal:LoginWallModal');
					break;
				case PLAYOUT_API_ERRORS.EVENT_NOT_PUBLISHED:
					dispatch('error/setPlayoutError', ERROR.EVENT_NOT_PUBLISHED_YET, { root: true });
					break;
				case PLAYOUT_API_ERRORS.EVENT_PUBLISH_ENDED:
					dispatch('error/setPlayoutError', ERROR.EVENT_PUBLISH_ENDED, { root: true });
					break;
				default:
					dispatch('error/setPlayoutError', ERROR.EVENT_PLAYOUT_FAILED, { root: true });
					console.error(error);
				}
			}
		},
		loadEventAds: async({ dispatch, rootGetters }, eventId) => {
			if (rootGetters['features/isAdsDisabled']) {
				return;
			}

			try {
				const ads = await api.getEventAds(eventId);
				dispatch('event/setAds', ads.data, { root: true });
			} catch (error) {
				if (error && error.constructor && error.constructor.name === 'ApiError') {
					console.error('Unable to load event ads', error);
				} else {
					console.log(error);
				}
			}
		},
		loadHighlightRules: async({ state, commit, getters }) => {
			if (state.highlightRules) {
				return;
			}

			try {
				const eventId = getters.event.id;
				const rules = await api.getEventHighlightRules(eventId);
				commit(SET_HIGHLIGHT_RULES, rules);
			} catch (error) {
				console.error(error);
			}
		},
		resetHighlightRules: ({ commit }) => {
			commit(SET_HIGHLIGHT_RULES, []);
		},
		createEventTag: async({ state, commit, rootGetters, getters }, { data, shouldSnap = true, backendSnap = false, tagGroupId = null }) => {
			const { taggingToolOnly, eventId, playerId = null, highlight, participantId, secondsAfter, secondsBefore, tagId, timeline, timestamp, name, tagTemplateItemId } = data;
			const selectedTagGroup = rootGetters['tagGroups/selectedTagGroup'];

			let payload = {
				tagGroupId: tagGroupId ?? selectedTagGroup.id,
				timestamp:  timestamp + (getters.tagToStreamOffset || 0),
				tagId,
				secondsBefore,
				secondsAfter,
				timeline,
				taggingToolOnly,
				highlight,
				playerId,
			};

			if (participantId) {
				if (state.event.participants.some(e => e.context === 'player' && e.participatable.id === participantId)) {
					payload = {
						...payload,
						playerId: participantId,
					};
				} else {
					payload = {
						...payload,
						teamId: participantId,
					};
				}
			}

			if (name) {
				payload = {
					...payload,
					name,
				};
			}

			if (tagTemplateItemId) {
				payload = {
					...payload,
					tagTemplateItemId,
				};
			}

			if (!Number.isFinite(payload.secondsAfter) || payload.secondsAfter < 0) {
				payload.secondsAfter = 0;
			}

			if (!Number.isFinite(payload.secondsBefore) || payload.secondsBefore < 0) {
				payload.secondsBefore = 0;
			}

			if (shouldSnap && backendSnap) {
				payload.useStreamImage = true;
			}

			if (data.value) payload.value = data.value;

			const tagType = rootGetters['tags/tags'].find(t => t.tag.id === tagId);
			if (tagType) {
				bus.publish(TAG_BEFORE_CREATE, { ...tagType, timestamp });
			}

			const tag = await api.createEventTag(eventId, payload);

			if (shouldSnap && !backendSnap) {
				const image = await playerBus.snap();
				await api.uploadEventTagImage(eventId, tag.id, image);
			}

			if (tag?.event && !hasTag(state.tags, tag.id)) {
				commit(ADD_EVENT_TAG, new EventTag(tag));
			}

			bus.publish(TAG_CREATE, tag);

			return tag;
		},
		updateEventOffset: async({ dispatch }, { eventId, offset }) => {
			await api.updateEventOffset(eventId, offset);
			await dispatch('loadEvent', { id: eventId });
			await dispatch('loadEventTags', { id: eventId });
		},
		updateEventTag: async({ state, commit }, { eventId, tagId, body, shouldSnap, backendSnap = false }) => {
			const { event } = state;

			if (shouldSnap) {
				if (backendSnap) {
					body.useStreamImage = true;
				} else {
					const image = await playerBus.snap();
					await api.uploadEventTagImage(eventId, tagId, image);
				}
			}

			const tag = await api.updateEventTag(eventId, tagId, body);
			const duration = event.durationSeconds;

			if ('timestamp' in body) {
				const value = body.timestamp;
				body.timestamp = Math.min(Math.max(value, 0), duration);

				if ('secondsBefore' in body) {
					const previousValue = body.secondsBefore;
					body.secondsBefore = Math.min(Math.max(previousValue, 0), body.timestamp);
				}

				if ('secondsAfter' in body) {
					const previousValue = body.secondsAfter;
					body.secondsAfter = Math.min(Math.max(previousValue, 0), duration - body.timestamp);
				}
			}

			commit(UPDATE_EVENT_TAG, tag);
		},
		updateEventTags: async({ commit }, { eventId, tags, body }) => {
			await Promise.all(tags.map(tag => api.updateEventTag(eventId, tag.id, body)));
			tags.map(tag => commit(UPDATE_EVENT_TAG, tag));
		},
		deleteEventTag: async({ state, commit }, tag) => {
			await api.deleteEventTag(state.event.id, tag.id);

			if (hasTag(state.tags, tag.id)) {
				commit(DELETE_EVENT_TAG, tag);
			}
		},
		deleteEventTags: async({ state, commit }, tags) => {
			await api.deleteEventTags(
				state.event.id,
				tags.map(tag => tag.id)
			);
			tags.map(tag => commit(DELETE_EVENT_TAG, tag));
		},
		setStream: async({ commit }, { index, stream }) => {
			commit(SET_STREAM, {
				index,
				value: stream,
			});
		},
		streamStartChanged: async() => {
			bus.publish(EVENT.STREAM_START_CHANGED);
		},
		timeShiftEventTags: async(_, { eventId, tagIds, offset }) => {
			await api.timeShiftEventTags(eventId, tagIds, offset);
		},
		handleEventUpdate: async({ commit, dispatch, getters }, { data, patch = false }) => {
			const event = data instanceof Event ? data : new Event(data);

			bus.publish(EVENT.EVENT_UPDATE, event.id);

			// set private visibility change flag
			if (
				getters.event &&
				event.id === getters.event.id &&
				event.visibility === EVENT_VISIBILITY.PRIVATE &&
				getters.event.visibility !== EVENT_VISIBILITY.PRIVATE
			) {
				event.wasMadePrivate = true;
			}

			if (patch) commit(PATCH_EVENT, data);
			else commit(SET_EVENT, event);

			// set the reference matchday event once, when the first event loads
			// so that when the event changes dynamically (through matchday list)
			// it will always show the same matches (will lock the matchday list)
			if (!getters.matchdayReferenceEvent) {
				commit(SET_MATCHDAY_REFERENCE_EVENT, event);
			}

			await dispatch('updateHeartbeat', event);

			dispatch('error/setPlayerError', null, { root: true });
		},
		handleEventTagUpdate: ({ commit, getters, state }, { event, tag }) => {
			const eventTag = new EventTag(tag);
			switch (event) {
			case 'EventTagCreated':
				commit(ADD_EVENT_TAG, eventTag);
				break;
			case 'EventTagUpdated':
				commit(UPDATE_EVENT_TAG, eventTag);
				break;
			case 'EventTagDeleted':
				commit(DELETE_EVENT_TAG, eventTag);
				break;
			default:
				throw new Error(`Unknown event: ${event}`);
			}

			// Re-calculate scoreHome & scoreGuest for scoring tags that happen after the current tag
			if (eventTag.isScoringTag) {
				const updateableTags = state.tags.filter((currentTag) => currentTag.isScoringTag);
				updateableTags.forEach((updateableTag) => {
					const filteredTags = updateableTags.filter(currentTag => currentTag.timestamp <= updateableTag.timestamp);
					const calculatedScore = { home: 0, guest: 0 };
					for (const currentTag of filteredTags) {
						const scoreTeam = getters.event.teamHome.id === currentTag.teamId ? 'home' : 'guest';
						calculatedScore[scoreTeam]++;
					}
					updateableTag.scoreHome = calculatedScore.home;
					updateableTag.scoreGuest = calculatedScore.guest;

					commit(UPDATE_EVENT_TAG, updateableTag);
				});
			}
		},
		loadMatchdayEvents: async({ commit, getters, dispatch }, force = false) => {
			if (!force && getters.isMatchdayEventsLoaded) return;

			const eventId = getters.matchdayReferenceEvent?.id || getters.event.id;
			const events = await api.getMatchdayEvents(eventId);
			commit(SET_MATCHDAY_EVENTS, events);

			await dispatch('sync/setupEventScoreListener', eventId, {
				root: true,
			});

			commit(SET_LOADED_STATUS, {
				name:   'matchdayEvents',
				status: true,
			});
		},
		updateScore: ({ commit }, data) => {
			commit(UPDATE_EVENT_SCORE, data);
		},
		loadVideos: () => {
			throw new Error('Not implemented.');
		},
		setScoreboardSettings: async({ commit, getters }, { showScore, showTime }) => {
			const data = await api.setScoreboardSettings(getters.event.id, showScore, showTime);
			commit(SET_SCOREBOARD_SETTINGS, data.scoreboardSettings);
		},
		setPeriodStartMinutes: async({ commit, getters }, value) => {
			const data = await api.setEventPeriodStartMinutes(getters.event.id, value);
			commit(SET_PERIODS, data.periods);
		},
		setCustomText: async({ commit, getters }, value) => {
			const data = await api.setCustomPlayerText(getters.event.id, value);
			commit(SET_PLAYER_CUSTOM_TEXT, data.playerCustomText);
		},
		setCustomTextEnabled: async({ commit, getters }, value) => {
			const data = await api.setCustomPlayerTextEnabled(getters.event.id, value);
			commit(SET_PLAYER_CUSTOM_TEXT_ENABLED, data.playerCustomTextEnabled);
		},
		setAction2mTag: ({ dispatch, getters, rootGetters }, { participant, timestamp, playerId = null }) => {
			const tag = rootGetters['tags/action_2m_tag'];
			if (!tag) {
				console.log('Skipping action due to missing action tag.');
				return;
			}

			const payload = {
				eventId:         getters.event.id,
				timestamp,
				playerId:        playerId,
				tagId:           tag.tag.id,
				secondsBefore:   tag.secondsBefore,
				secondsAfter:    tag.secondsAfter,
				timeline:        tag.timeline,
				taggingToolOnly: tag.taggingToolOnly,
				highlight:       tag.highlight,
				participantId:   participant.participatable.id,
			};

			dispatch('createEventTag', { data: payload });
		},
		setAction5mTag: ({ dispatch, getters, rootGetters }, { participant, timestamp, playerId = null }) => {
			const tag = rootGetters['tags/action_5m_tag'];
			if (!tag) {
				console.log('Skipping action due to missing action tag.');
				return;
			}

			const payload = {
				eventId:         getters.event.id,
				timestamp,
				playerId:        playerId,
				tagId:           tag.tag.id,
				secondsBefore:   tag.secondsBefore,
				secondsAfter:    tag.secondsAfter,
				timeline:        tag.timeline,
				taggingToolOnly: tag.taggingToolOnly,
				highlight:       tag.highlight,
				participantId:   participant.participatable.id,
			};

			dispatch('createEventTag', { data: payload });
		},
		increaseScore: ({ dispatch, getters, rootGetters }, { participant, timestamp, playerId = null }) => {
			const tag = rootGetters['tags/scoreTag'](getters.event.sport.name);
			if (!tag) {
				console.log('Skipping action due to missing score tag.');
				return;
			}

			const payload = {
				eventId:         getters.event.id,
				timestamp,
				playerId:        playerId,
				tagId:           tag.tag.id,
				secondsBefore:   tag.secondsBefore,
				secondsAfter:    tag.secondsAfter,
				timeline:        tag.timeline,
				taggingToolOnly: tag.taggingToolOnly,
				highlight:       tag.highlight,
				participantId:   participant.participatable.id,
			};

			dispatch('createEventTag', { data: payload });
		},
		startClock: ({ getters, rootGetters, dispatch }, { timestamp }) => {
			const tag = rootGetters['tags/clockStartTag'];
			if (!tag) {
				console.log('Skipping action due to missing clock_start tag.');
				return;
			}

			const tagGroup = rootGetters['tagGroups/defaultTagGroup'];
			const payload = {
				eventId:         getters.event.id,
				timestamp,
				tagId:           tag.tag.id,
				secondsBefore:   tag.secondsBefore,
				secondsAfter:    tag.secondsAfter,
				timeline:        tag.timeline,
				taggingToolOnly: tag.taggingToolOnly,
				highlight:       tag.highlight,
			};

			dispatch('createEventTag', { data: payload, tagGroupId: tagGroup.id });
		},
		stopClock: ({ getters, rootGetters, dispatch }, { timestamp, value }) => {
			const tag = rootGetters['tags/clockStopTag'];
			if (!tag) {
				console.log('Skipping action due to missing clock_end tag.');
				return;
			}

			const tagGroup = rootGetters['tagGroups/defaultTagGroup'];
			const payload = {
				eventId:         getters.event.id,
				timestamp,
				tagId:           tag.tag.id,
				secondsBefore:   tag.secondsBefore,
				secondsAfter:    tag.secondsAfter,
				timeline:        tag.timeline,
				taggingToolOnly: tag.taggingToolOnly,
				highlight:       tag.highlight,
				value,
			};

			dispatch('createEventTag', { data: payload, tagGroupId: tagGroup.id });
		},
		setClock: ({ getters, rootGetters, dispatch }, { timestamp, value }) => {
			const tag = rootGetters['tags/clockSetTag'];
			if (!tag) {
				console.log('Skipping action due to missing clock_set tag.');
				return;
			}

			const tagGroup = rootGetters['tagGroups/defaultTagGroup'];
			const payload = {
				eventId:         getters.event.id,
				timestamp,
				tagId:           tag.tag.id,
				secondsBefore:   tag.secondsBefore,
				secondsAfter:    tag.secondsAfter,
				timeline:        tag.timeline,
				taggingToolOnly: tag.taggingToolOnly,
				highlight:       tag.highlight,
				value,
			};

			dispatch('createEventTag', { data: payload, tagGroupId: tagGroup.id });
		},
		setStreamLoaded() {
			if (window.eventBus) {
				window.eventBus.$emit('streamLoaded');
			}
		},
		loadEventBannerImage: async({ getters, commit }) => {
			const eventBannerAd = getters.ads.find(ad => ad.type === AdType.EVENT_BANNER);
			if (!eventBannerAd || !eventBannerAd.imageUrl) {
				return;
			}
			try {
				const response = await fetch(eventBannerAd.imageUrl);
				commit(SET_EVENT_BANNER_IMAGE_URL, response.url);
			} catch (error) {
				console.error(error);
			}
		},
		updateHeartbeat(_context, event) {
			heartbeat.setEventId(event.id);
		},
		setGeoblocked: async({ commit }) => {
			commit(SET_GEOBLOCKED);
		},
		setPreviewLimitReached: async({ commit }) => {
			commit(SET_PREVIEW_LIMIT_REACHED);
		},
		setAds: async({ commit }, ads) => {
			commit(SET_EVENT_ADS, ads);
		},
		getTagTemplates: async({ commit, rootGetters }) => {
			const event = rootGetters['event/event'];
			const tagTemplates = await api.getTagTemplates(event.id);
			commit(SET_TAG_TEMPLATES, tagTemplates);
		},
		deleteEntryOnTagTemplate: async({ rootGetters, commit, getters }, { templateId, tagId }) => {
			const event = rootGetters['event/event'];
			await api.deleteEntryOnTagTemplate(event.id, templateId, tagId);

			const tagTemplates = getters.tagTemplates;
			tagTemplates.forEach((template) => {
				if (template.id === parseInt(templateId)) {
					template.items = template.items?.filter(item => item.id !== parseInt(tagId)) ?? [];
				}
			});

			commit(SET_TAG_TEMPLATES, tagTemplates);
		},
	},
	getters: {
		event: (state) => state.event,
		tags:  (state, getters, rootGetters) => {
			try {
				const periods = getPeriods({ event: state.event, tags: state.tags });
				const tags = state.tags
					.map((tag) => {
						tag.team = getters.teams.find((team) => team.id === tag.teamId);
						tag.player = getters.players.find((player) => player.id === tag.playerId) || null;

						if (!tag.processed) {
							// If the stream started after the event, move the tags back the duration between event and stream start
							tag.originalTimestamp = tag.timestamp;
							tag.timestamp = tag.timestamp - (getters.tagToStreamOffset || 0);

							// Keep track of original visibility attributes, as they might be overwritten based on event review state
							tag.originalTimeline = tag.timeline;
							tag.originalAnalyisOnly = tag.analyisOnly;
						}

						if (Number.isFinite(tag.gameTimeSeconds)) {
							tag.gameTime = gameSecondsToGameTime(tag.gameTimeSeconds);
						} else {
							tag.gameTime = getGameTime({ event: getters.event, position: tag.originalTimestamp, tags: state.tags, periods });
						}

						if (tag.isOutsideVideo) {
							// Hide tags outside video time in timeline
							tag.timeline = false;
						}

						// If the event has tags hidden (needs review, but not reviewed, sets tags to hidden in sidebar and timeline)
						// Also check if we enforce certain tags to be shown in the timeline via URL parameter showTag
						const isTaggingTool = rootGetters.app.appType === APP_TYPE.TAGGING_TOOL;
						if (!state.event.tagsVisible && !isTaggingTool && !(rootGetters.features?.features?.timeline?.showTags || []).includes(tag.tag.name)) {
							tag.timeline = false;
							tag.taggingToolOnly = true;
						} else {
							// Set the visibility attributes to the original values, in case the event was set back from 'reviewed' to 'needs review'
							tag.timeline = tag.originalTimeline;
							tag.taggingToolOnly = tag.originalAnalyisOnly;
						}

						tag.processed = true;
						return tag;
					});

				const filteredTagCount = tags.filter(tag => tag.timestamp < 0).length;
				if (filteredTagCount > 0) {
					console.log('Warning: ' + filteredTagCount + ' tags filtered out because the video starts ' + getters.streamLagTimeSeconds + ' seconds after event start.');
				}

				return tags;
			} catch (_e) {
				console.error(_e);
				return [];
			}
		},
		hasTagsWithIQScore: (state) => {
			return state.tags.some((tag) => tag.score > 0);
		},
		eventTagsForEvent: (state, getters, rootState, rootGetters) => {
			return getters.tags.filter((evTag) => {
				return rootGetters['tags/isForEvent'](rootGetters['tags/tagById'](evTag.tag.id)) && !evTag.playerId && !evTag.teamId;
			});
		},
		eventTagsForParticipant: (state, getters, rootState, rootGetters) => {
			return getters.tags.filter(
				(evTag) => rootGetters['tags/isForParticipant'](rootGetters['tags/tagById'](evTag.tag.id)) || Boolean(evTag.playerId) || Boolean(evTag.teamId)
			);
		},
		tagById: (state) => (id) => {
			if (!id) {
				return null;
			}

			return state.tags.find((tag) => tag.id === id);
		},
		tagsByIds: (state) => (ids) => {
			if (!ids) {
				return [];
			}
			return state.tags.filter((tag) => ids.includes(tag.id));
		},
		hasTags:      (state) => state.tags.length > 0,
		timelineTags: (state, getters, rootState, rootGetters) => {
			return state.tags.filter((tag) => {
				return tag.timeline || (rootGetters.features?.features?.timeline?.showTags || []).includes(tag.tag.name);
			});
		},
		playerTags:        (state) => state.tags.filter((tag) => !tag.taggingToolOnly),
		couchTags:         (_, getters) => getters.timelineTags.filter((tag) => tag.duration > 0),
		hasTimelineItems:  (state, getters) => getters.tags.some((tag) => tag.timeline),
		stream:            (state) => state.stream,
		streamURL:         (state) => (state.stream ? state.stream.hlsUrl : null),
		streamURLNonDvr:   (state) => (state.stream ? state.stream.hlsNoDvrUrl : null),
		cmcdContentId:     (state) => state.stream?.cmcdContentId ?? null,
		tagToStreamOffset: (state, getters) => (!getters.vodAvailable ? getters.streamLagTimeSeconds : 0),
		isLive:            (state) => {
			// Compute isLive reactively based on current time and event timestamps
			// This ensures it updates automatically when the event ends, even without a backend update
			if (!state.event || state.event.vodOnly) return false;
			if (!state.event.startTimestamp || !state.event.endTimestamp) return state.event.isLive;

			// Use unixTime if available (updates every second), otherwise use current time
			const currentTime = state.unixTime ?? Math.floor(Date.now() / 1000);
			return currentTime >= state.event.startTimestamp && currentTime <= state.event.endTimestamp;
		},
		isUpcoming:              (state) => state.event.liveInSeconds > 0,
		isVod:                   (_, getters) => !getters.isLive,
		vodAvailable:            (state) => state.event?.streams && state.event?.streams[0].vodStatus === 'available',
		videoAvailable:          (state) => state.event?.videoAvailable,
		isDownloadable:          (state) => state.event?.downloadable,
		isPublic:                (state) => state.event.isPublic,
		isPrivate:               (_, getters) => !getters.isPublic,
		childCategory:           (state) => state.event.categoryChild,
		sport:                   (state) => state.event?.sport,
		sportCategoryParents:    (state) => state.event?.sport?.categoryParents ? state.event?.sport?.categoryParents : null,
		sportName:               (state) => (state.event?.sport ? state.event.sport.name : ''),
		sportId:                 (state) => state.event?.sport?.id,
		participantType:         (state) => (state.event.sport ? state.event.sport.participatableContext.name : null),
		isPlayerCompetition:     (state) => state.event.isPlayerCompetition,
		isTeamCompetition:       (state) => state.event.isTeamCompetition,
		isConcurrentCompetition: (state) => state.event.isConcurrentCompetition,
		isSequentialCompetition: (state) => state.event.isSequentialCompetition,
		participants:            (state) => {
			if (!state.event) return [];
			return state.event.participants.map((participant) => participant.participatable);
		},
		participantById:       (_, getters) => (id) => getters.participants.find((participant) => participant.id === id),
		// Get delay of event start compared to stream start
		streamLeadTimeSeconds: (state, getters) => {
			if (state.event.status === 'ended' && getters.vodAvailable) {
				return 0;
			}
			if (Array.isArray(state.event.streams) && state.event.streams.length > 0) {
				const firstStream = state.event.streams[0];
				const streamStartedAt = firstStream.startedAtTimestamp;

				// Only calculate offsetEventStartToStreamStart if streamStartedAt is defined
				if (streamStartedAt) {
					const offsetEventStartToStreamStart = state.event.startTimestamp - streamStartedAt;
					// If the offset is 0 or negative, return 0. Otherwise, return the offset.
					return offsetEventStartToStreamStart > 0 ? offsetEventStartToStreamStart : 0;
				}
			}
			return 0;
		},
		// Get delay of stream start compared to event start
		streamLagTimeSeconds: (state, getters) => {
			if (state.event.status === 'ended' && getters.vodAvailable) {
				return 0;
			}
			if (state.stream && state.stream.startedAt) {
				return Math.max(moment(state.stream.startedAt).diff(state.event.start, 'seconds'), 0);
			}
			return 0;
		},
		highlightRules: state => state.highlightRules,
		lineup:         state => state.lineup,
		league:         (state) => {
			if (!state.event) return null;
			return state.event.league;
		},
		eventStats:               state => state.eventStats,
		eventStatsFormats:        state => state.eventStats?.meta?.formats || [], // TODO
		lineupStatsPlayerEntries: state => state.eventStats?.data.filter(entry => Boolean(entry.eventLineup?.player)) || [],
		leagueTable:              state => state.leagueTable,
		teamRankings:             state => (state.leagueTable && state.event.participants.length === 2
			? state.leagueTable.entries.data
				.filter(entry => entry.team.id === state.event.teamHome.id || entry.team.id === state.event.teamGuest.id)
				.map(entry => entry.rank)
			: null),
		matchdayEvents:       state => state.matchdayEvents,
		matchdayEventChanged: state => state.matchdayEventChanged,
		isLeagueMatch:        (getters) => getters.childCategory === CHILD_MATCH_LEAGUE && getters.league !== null,
		eventPlayerStats:     (_, getters) => (playerId) => {
			const statsEntry = getters.lineupStatsPlayerEntries.find(entry => entry?.eventLineup?.player?.id === playerId);
			if (!statsEntry) {
				return null;
			}
			const statsFormat = getters.eventStatsFormats.find(format => format.dataType === statsEntry.dataType && format.format === 'tableSmall');
			if (!statsFormat) return null;
			return {
				format: statsFormat,
				data:   pick(statsEntry.data, statsFormat.properties),
			};
		},
		eventURL:               state => state.event.url,
		eventTitle:             ({ event }) => event.name,
		shareLinks:             ({ event }) => event.shareLinks,
		isMatchdayEventsLoaded: state => Boolean(state.loadedStatus.matchdayEvents),
		matchdayReferenceEvent: state => state.matchdayReferenceEvent,
		players(state) {
			return state.event.participants?.reduce((all, current) => {
				const toAdd = current.context === 'team' ? current.participatable.players ?? [] : [current.participatable];
				return [...all, ...toAdd];
			}, []);
		},
		teams:                       state => state.event.participants?.map(participiant => participiant.participatable),
		showScoreboardScore:         (state, getters, rootGetters) => Boolean(getters.scoreboardSettings?.showScore) && (state.event.tagsVisible || rootGetters.app.appType === APP_TYPE.TAGGING_TOOL),
		showScoreboardTime:          (state, getters, rootGetters) => Boolean(getters.scoreboardSettings?.showTime) && (state.event.tagsVisible || rootGetters.app.appType === APP_TYPE.TAGGING_TOOL),
		scoreboardSettings:          state => state.event?.scoreboardSettings,
		hasScoreboardSupportedSport: (state, getters) => Object.values(GAME_SPORT).includes(getters.sportName) && SCOREBOARD_SUPPORTED_PARENT_CATEGORIES.includes(state.event.categoryParent),
		periodStartMinutes:          ({ event }) => {
			return event.periods
				.sort((a, b) => a.timestamp - b.timestamp)
				.filter(period => period.type === PERIOD_TYPE.ACTION)
				.map(period => period.startMinutes);
		},
		password:                  state => state.password,
		unixTime:                  state => state.unixTime,
		customText:                (state) => state.event?.playerCustomText,
		customTextEnabled:         (state) => state.event?.playerCustomTextEnabled || false,
		customOverlayImage:        (state) => state.event?.playerCustomOverlaySettings?.imageFileName,
		customOverlayImageEnabled: (state) => {
			const settings = state.event?.playerCustomOverlaySettings;
			return (settings?.enabled && settings?.inPlayer && settings?.imageFileName) || false;
		},
		canUpdateEvent:           (state) => state.eventPermissions?.some(p => p.key === 'update' && p.value === true) || false,
		eventPermissions:         (state) => state.eventPermissions,
		eventBannerImageUrl:      (state) => state.eventBannerImageUrl,
		isPaymentProtected:       (state) => state.event?.isPaymentProtected,
		isLoginProtected:         (state) => state.event?.isLoginProtected,
		isGeoblocked:             (state) => state.isGeoblocked,
		requiresLoginForCYM:      (state) => state.event?.arenaFeatureAccess?.isClipYourMomentRestricted,
		requiresLoginForDownload: (state) => state.event?.arenaFeatureAccess?.isDownloadMediaRestricted,
		ads:                      (state) => state.ads,
		adBanner:                 (state) => state.ads.find(ad => ad.type === AdType.EVENT_BANNER),
		tagTemplates:             (state) => state.tagTemplates,
		previewLimitReached:      (state) => state.previewLimitReached,
		productPlacementEnabled:  (state) => Boolean(state.event?.productPlacementEnabled),
	},
};
